import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const PARABLE_IMG =
  "https://images.unsplash.com/photo-1516708274537-6f91e34ccaf2?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0&w=1400";

const PARABLE_LINES = [
  "In 1952, on the island of Koshima,",
  "scientists fed monkeys sweet potatoes dropped in the sand.",
  "The monkeys liked the taste — but hated the dirt.",
  "An eighteen-month-old female named Imo discovered",
  "she could wash the potatoes in a nearby stream.",
  "She taught her mother. Her playmates taught theirs.",
  "Then — on an unknown autumn morning in 1958 —",
  "the hundredth monkey learned.",
  "By that evening, the entire tribe was washing potatoes.",
  "And then, across the sea, monkeys on other islands began doing the same.",
];

const KEY_LINE = "the hundredth monkey learned.";

const LINE_REVEAL = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.6 },
};

// Pre-computed per-line transitions — static data, no per-render object creation.
const LINE_TRANSITIONS = PARABLE_LINES.map((_, i) => ({
  duration: 0.6,
  delay: i * 0.05,
}));

const QUOTE_REVEAL = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.4 },
  transition: { duration: 0.7 },
};

function ParableSticky() {
  return (
    <div className="md:sticky md:top-24">
      <div className="eyebrow mb-4">The Parable · 1952 — 1958</div>
      <h2 className="font-display uppercase font-bold text-5xl md:text-7xl leading-[0.9] tracking-tight">
        Imo<br />and the<br />Hundredth<br />Monkey.
      </h2>
      <div className="relative mt-10 border-2 border-current overflow-hidden">
        <img
          src={PARABLE_IMG}
          alt="A macaque on a rocky shore — symbolic of the Koshima troop"
          className="w-full h-[360px] object-cover duotone"
          loading="lazy"
        />
        <div className="absolute bottom-3 right-3 bg-[hsl(var(--background))] border-2 border-current px-3 py-1 font-display uppercase tracking-widest text-[0.7rem]">
          Koshima Island · Macaca fuscata
        </div>
      </div>
    </div>
  );
}

function ParableLines() {
  return (
    <>
      {PARABLE_LINES.map((line, i) => (
        <motion.p
          key={line}
          {...LINE_REVEAL}
          transition={LINE_TRANSITIONS[i]}
          className={
            "font-serif-display leading-tight " +
            (line === KEY_LINE
              ? "text-4xl md:text-6xl font-bold text-[hsl(var(--primary))]"
              : "text-xl md:text-2xl")
          }
        >
          {line}
        </motion.p>
      ))}
    </>
  );
}

function ParableQuote() {
  return (
    <motion.blockquote
      {...QUOTE_REVEAL}
      className="border-l-4 border-[hsl(var(--primary))] pl-6 mt-10"
    >
      <p className="font-body italic text-xl leading-relaxed">
        "When a certain critical number achieves an awareness, this new
        awareness may be communicated from mind to mind."
      </p>
      <footer className="eyebrow mt-3">Ken Keyes, Jr.</footer>
    </motion.blockquote>
  );
}

export default function ParableSection() {
  return (
    <section
      className="relative border-b-2 border-current"
      data-testid="parable-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid gap-14 md:grid-cols-[1.1fr_1fr] items-start">
        <ParableSticky />
        <div className="space-y-7 md:pt-2">
          <ParableLines />
          <ParableQuote />
          <Link
            to="/read#the-story"
            className="btn-brutal mt-6"
            data-testid="parable-read-cta"
          >
            Read the full parable
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}
